// import { FC } from 'react';
// import { useNavigate, useParams } from 'react-router-dom';
// import { AddButton } from '../../../../../../lib/components/Buttons/AddButton';
// import { ButtonsContainer } from '../../../../../../lib/components/Buttons/ButtonsContainer';
// import { RemoveButton } from '../../../../../../lib/components/Buttons/RemoveButton';
// import { CellStyle } from '../../../../../../lib/components/DataTable/CellStyle';
// import { ColumnFactory } from '../../../../../../lib/components/DataTable/ColumnFactory';
// import { DataTable } from '../../../../../../lib/components/DataTable/DataTable';
// import { useRequestFeedback } from '../../../../../../lib/hooks';
// import { ChapterEntity } from '../../../../../../lib/model/Book';
// import { extract_num_id_from_id } from '../../../utils/extract_num_id_from_id';
// import { useChaptersApi } from '../hooks/useChaptersApi';

// interface IProps {
// 	chapters: ChapterEntity[];
// 	on_remove_success: () => void;
// }

// export const ChaptersDataTable: FC<IProps> = ({ chapters, on_remove_success }) => {
// 	const { id, edition_id } = useParams() as {
// 		id: string;
// 		edition_id: string;
// 		chapter_id: string;
// 	};

// 	const book_num_id = extract_num_id_from_id(id);
// 	const edition_num_id = extract_num_id_from_id(edition_id);

// 	const BASE_URL = `${process.env.REACT_APP_BOOKS_API}/books/${book_num_id}/editions/${edition_num_id}/chapters`;

// 	const navigate = useNavigate();
// 	const { remove } = useChaptersApi();

// 	if (remove.result) {
// 		on_remove_success();
// 	}

// 	useRequestFeedback(remove.result, remove.error);

// 	return (
// 		<DataTable
// 			{...{
// 				rowData: [...chapters].sort((a, b) => a.order - b.order),
// 				buttons: [<AddButton {...{ onClick: () => navigate('novo') }} />],
// 				columns: [
// 					ColumnFactory.Link(
// 						{ field: 'title', headerName: 'Titulo', resizable: true, width: 200 },
// 						(props: { data: { id: string } }) => props.data.id
// 					),
// 					{
// 						field: 'chapter_type',
// 						headerName: 'Tipo',
// 						resizable: false,
// 						width: 100,
// 					},
// 					{
// 						field: 'language',
// 						headerName: 'Idioma',
// 						resizable: false,
// 						width: 100,
// 					},
// 					{
// 						field: 'authors',
// 						headerName: 'Autor(es)',
// 						resizable: true,
// 						width: 240,
// 					},
// 					{
// 						col_def: {
// 							field: 'buttons',
// 							headerName: '',
// 							pinned: 'right',
// 							cellStyle: CellStyle.Centered,
// 							width: 180,
// 						},
// 						renderer: (props: { data: { num_id: string; title: string } }) => (
// 							<ButtonsContainer>
// 								<RemoveButton
// 									{...{
// 										style: { marginLeft: '.5rem' },
// 										on_confirm: () => remove.do_remove(props.data.num_id),
// 										title: `Deseja remover o livro ${props.data.title}?`,
// 									}}
// 								/>
// 							</ButtonsContainer>
// 						),
// 					},
// 				],
// 			}}
// 		/>
// 	);
// };
import { FC } from 'react';
import { Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useNavigate, useParams } from 'react-router-dom';

import { AddButton } from '../../../../../../lib/components/Buttons/AddButton';
import { ButtonsContainer } from '../../../../../../lib/components/Buttons/ButtonsContainer';
import { RemoveButton } from '../../../../../../lib/components/Buttons/RemoveButton';
import { CellStyle } from '../../../../../../lib/components/DataTable/CellStyle';
import { ColumnFactory } from '../../../../../../lib/components/DataTable/ColumnFactory';
import { DataTable } from '../../../../../../lib/components/DataTable/DataTable';
import { useRequestFeedback } from '../../../../../../lib/hooks';
import { ChapterEntity } from '../../../../../../lib/model/Book';
import { extract_num_id_from_id } from '../../../utils/extract_num_id_from_id';
import { useChaptersApi } from '../hooks/useChaptersApi';

interface IProps {
	chapters: ChapterEntity[];
	on_remove_success: () => void;
}

export const ChaptersDataTable: FC<IProps> = ({ chapters, on_remove_success }) => {
	const { id, edition_id } = useParams() as {
		id: string;
		edition_id: string;
		chapter_id: string;
	};

	const book_num_id = extract_num_id_from_id(id);
	const edition_num_id = extract_num_id_from_id(edition_id);

	const BASE_URL = `${process.env.REACT_APP_BOOKS_API}/books/${book_num_id}/editions/${edition_num_id}/chapters`;

	const navigate = useNavigate();
	const { remove } = useChaptersApi();

	if (remove.result) {
		on_remove_success();
	}

	useRequestFeedback(remove.result, remove.error);

	const buildTreeData = (items: any[]): DataNode[] => {
		return items.map((item) => ({
			key: item.id ?? item.num_id ?? item.title,

			title: <span>{item.title}</span>,

			children: item.items && item.items.length > 0 ? buildTreeData(item.items) : undefined,
		}));
	};

	return (
		<DataTable
			{...{
				rowData: [...chapters].sort((a, b) => a.order - b.order),

				buttons: [
					<AddButton
						{...{
							onClick: () => navigate('novo'),
						}}
					/>,
				],

				columns: [
					ColumnFactory.Link(
						{
							field: 'title',
							headerName: 'Titulo',
							resizable: true,
							flex: 2,
							minWidth: 220,
						},
						(props: { data: { id: string } }) => props.data.id
					),

					{
						col_def: {
							field: 'items_tree',
							headerName: 'Sumário do capítulo',
							resizable: true,
							flex: 3,
							minWidth: 350,
							autoHeight: true,
						},

						renderer: (props: any) => {
							const items = props.data.items;

							if (!items || items.length === 0) {
								return <span>-</span>;
							}

							return (
								<div
									style={{
										paddingTop: 8,
										paddingBottom: 8,
									}}
								>
									<Tree
										treeData={buildTreeData(items)}
										selectable={false}
										showLine
										defaultExpandAll={true}
										blockNode={false}
									/>
								</div>
							);
						},
					},

					{
						field: 'chapter_type',
						headerName: 'Tipo',
						resizable: false,
						flex: 1,
						minWidth: 120,
					},

					{
						field: 'language',
						headerName: 'Idioma',
						resizable: false,
						flex: 1,
						minWidth: 100,
					},

					{
						field: 'authors',
						headerName: 'Autor(es)',
						resizable: true,
						flex: 2,
						minWidth: 240,
					},

					{
						col_def: {
							field: 'buttons',
							headerName: '',
							pinned: 'right',
							cellStyle: CellStyle.Centered,
							width: 180,
						},

						renderer: (props: {
							data: {
								num_id: string;
								title: string;
							};
						}) => (
							<ButtonsContainer>
								<RemoveButton
									{...{
										style: {
											marginLeft: '.5rem',
										},

										on_confirm: () => remove.do_remove(props.data.num_id),

										title: `Deseja remover o livro ${props.data.title}?`,
									}}
								/>
							</ButtonsContainer>
						),
					},
				],
			}}
		/>
	);
};
